/**
 *
 * @param {string[]} primaryStudyInstanceUIDs
 * @param {object[]} studyDisplayList
 * @param {string} studyDisplayList.studyInstanceUid
 * @param {string} studyDisplayList.date
 * @param {string} studyDisplayList.description
 * @param {string} studyDisplayList.modalities
 * @param {number} studyDisplayList.numInstances
 * @param {object[]} displaySets
 * @param {number} recentTimeframe - The number of milliseconds to consider a study recent
 * @returns tabs - The prop object expected by the StudyBrowser component
 */

export function createStudyBrowserTabs(
  primaryStudyInstanceUIDs,
  studyDisplayList,
  displaySets,
  recentTimeframeMS = 31536000000,
  // Equine: optional. 3.8.0 sorted each study's display sets with the hanging
  // protocol's sort function inside the (then panel-local) version of this
  // helper; 3.9 dropped the sort when promoting the helper to platform/core.
  // Our virtual series carry the catalogue view order in SeriesNumber, so
  // without this the thumbnail panel lists a lot's 36 views in display-set
  // creation order rather than catalogue order. Pass the service to restore
  // the 3.8.0 ordering; omit it and behaviour matches upstream 3.9.
  hangingProtocolService = null,
  clinicalDisplaySetSort = null
) {
  const primaryStudies = [];
  const allStudies = [];

  // Equine: "Extras" are projections outside the catalogued set — the
  // dicomweb-proxy labels them "Extras — {label}". Vets want their catalogued
  // views first and anything extra grouped below.
  const isExtra = ds =>
    typeof ds.SeriesDescription === 'string' && ds.SeriesDescription.startsWith('Extras — ');

  studyDisplayList.forEach(study => {
    const displaySetsForStudy = displaySets.filter(
      ds => ds.StudyInstanceUID === study.studyInstanceUid
    );

    if (clinicalDisplaySetSort) {
      displaySetsForStudy.sort(clinicalDisplaySetSort);
    } else if (hangingProtocolService) {
      displaySetsForStudy.sort(hangingProtocolService.getDisplaySetSortFunction());
    }

    const orderedSets = [
      ...displaySetsForStudy.filter(ds => !isExtra(ds)),
      ...displaySetsForStudy.filter(isExtra),
    ];

    const tabStudy = Object.assign({}, study, {
      displaySets: orderedSets,
      clinicalOrder: Boolean(clinicalDisplaySetSort),
    });

    if (primaryStudyInstanceUIDs.includes(study.studyInstanceUid)) {
      primaryStudies.push(tabStudy);
    }
    allStudies.push(tabStudy);
  });

  const primaryStudiesTimestamps = primaryStudies
    .filter(study => study.date)
    .map(study => new Date(study.date).getTime());

  const recentStudies =
    primaryStudiesTimestamps.length > 0
      ? allStudies.filter(study => {
          const oldestPrimaryTimeStamp = Math.min(...primaryStudiesTimestamps);

          if (!study.date) {
            return false;
          }
          const studyTimeStamp = new Date(study.date).getTime();
          return oldestPrimaryTimeStamp - studyTimeStamp < recentTimeframeMS;
        })
      : [];

  // Newest first
  const _byDate = (a, b) => {
    const dateA = Date.parse(a);
    const dateB = Date.parse(b);

    return dateB - dateA;
  };
  const tabs = [
    {
      name: 'primary',
      label: 'Primary',
      studies: primaryStudies.sort((studyA, studyB) => _byDate(studyA.date, studyB.date)),
    },
    {
      name: 'recent',
      label: 'Recent',
      studies: recentStudies.sort((studyA, studyB) => _byDate(studyA.date, studyB.date)),
    },
    {
      name: 'all',
      label: 'All',
      studies: allStudies.sort((studyA, studyB) => _byDate(studyA.date, studyB.date)),
    },
  ];

  return tabs;
}
